<template>
    <div class="mx-2">
        <div class="md-layout md-gutter">
            <div class="md-layout-item md-size-100">

                <div class="intervention-details__card">
                    <p class="font-medium mb-2 text-sm">Tableau de bord</p>
                    <md-divider class="mb-1 -mt-1 -mx-2"></md-divider>
	                <div class="leading-4 mb-1 text-sm">
		                <span class="font-medium">Bienvenue sur l'application mobile de</span>
		                <span class="text-primary font-bold ml-1">Gaia</span>
	                </div>
	                <div class="leading-4 text-sm">
		                <span class="font-medium">Version actuelle :</span>
		                <span class="text-primary font-bold text-primary ml-1">{{ cacheVersion }}</span>
	                </div>
                </div>

                <div class="intervention-details__card mt-2">
                    <p class="font-medium mb-2 text-sm">État des données hors-ligne</p>
                    <md-divider class="mb-2 -mt-1 -mx-2"></md-divider>
                    <div class="leading-4 mb-1 text-sm">
                        <span class="font-medium">Planning 6 jours :</span>
                        <span class="font-medium ml-1" v-if="loading">Chargement ...</span>
                        <span class="font-medium ml-1 text-success" v-else-if="planning.all">Disponible</span>
                        <span class="font-medium ml-1 text-danger" v-else>Indisponible</span>
                    </div>
                    <div class="leading-4 mb-1 text-sm">
                        <span class="font-medium">Journée et lendemain :</span>
                        <span class="font-medium ml-1" v-if="loading || loadingDay">Chargement ...</span>
                        <span class="font-medium ml-1 text-success" v-else-if="planning.todayAndTomorrow">Disponible</span>
                        <span class="font-medium ml-1 text-danger" v-else>Indisponible</span>
                    </div>
                    <div class="leading-4 text-sm">
                        <span class="font-medium">Modifications hors-ligne :</span>
                        <span class="ml-1 font-medium">{{ requestsCount > 0 ? `${requestsCount} à envoyer` : 'Aucune' }}</span>
                    </div>
                    <div class="leading-none mt-0.5 mb-1">
                        <span class="italic">Nombre de modifications effectuées hors connexion non synchronisées.</span>
                    </div>
                </div>

	            <div class="intervention-details__card mt-2">
		            <p class="font-medium mb-2 text-sm">Actions</p>
		            <md-divider class="mb-2 -mt-1 -mx-2"></md-divider>
		            <div class="leading-4 mb-2 text-sm">
			            <span class="font-medium">Récupération des données</span>
		            </div>
                    <div class="pb-2 flex justify-between">
                        <md-button class="md-icon-button md-raised md-success my-0 w-max" @click="getTodayAndTomorrow"
                                   :disabled="requestsCount > 0 || !isOnline">
                            <span class="leading-none font-medium">Mise à jour 2j</span>
                        </md-button>
			            <md-button class="md-icon-button md-raised md-info my-0 w-max" @click="getPlanning"
			                       :disabled="requestsCount > 0 || !isOnline">
				            <span class="leading-none font-medium">Mise à jour 6j</span>
			            </md-button>
		            </div>
		            <p class="italic font-medium leading-4 px-0.5 mt-0.5">
			            La récupération des données n'est possible que si il n'y a aucune modifications en attente.
		            </p>
		            <md-divider class="mb-1 mt-1 -mx-2"></md-divider>
		            <p class="leading-4 font-medium italic px-0.5">Les actions sont uniquement disponible quand le réseau est disponible.</p>
	            </div>

            </div>
        </div>

        <div class="calendar-overlay opacity-100 fixed z-50 w-full h-full" v-if="loading || loadingDay || loadingCache">
            <div class="h-full w-full flex items-center justify-center">
	            <md-progress-spinner class="md-white" md-mode="indeterminate"
	                                 :md-diameter="65" :md-stroke="5"></md-progress-spinner>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";

import indexedDBMixin from "@/mixins/indexed-db-mixin";
import applicationVersionMixin from "@/mixins/application-version-mixin";
import cacheMixin from "@/mixins/cache-mixin";

export default {
    name: "ProgressiveWebAppDashboard",
	mixins: [indexedDBMixin, applicationVersionMixin, cacheMixin],
	props: {
		isOnline: Boolean,
		isLoading: Boolean,
		requestsCount: Number
	},
    data() {
        return {
            loading: false,
            loadingDay: false,
            loadingCache: false,
	        planning: {
				all: null,
		        todayAndTomorrow: null,
	        },
            allOfflineData: null,
            dayOfflineData: null,
            cacheVersion: ''
        }
    },
    computed: mapState({
        technician: state => state.pwaAuth.technician,
    }),
    methods: {
        async getTodayAndTomorrow() {
            this.loadingDay = true
            const localStoragePwa = JSON.parse(localStorage.getItem('pwa'))
            try {
                // Check if last update is older than today
	            if (this.$moment(localStoragePwa.synchronization, 'DD/MM/YYYY - HH:mm').isBefore(this.$moment().startOf('day'))) {
                    // If so then clear everything in data table
                    await this.clearIndexedDBTable('data')
                }
                await this.$store.dispatch('pwaPlanning/getPlanning', {
                    access_token: localStoragePwa.access_token,
                    technician_id: this.technician.id,
	                period: 2
                }).then(async (response) => {
	                localStorage.setItem('pwa', JSON.stringify({
		                isAuthenticated: localStoragePwa.isAuthenticated,
		                access_token: localStoragePwa.access_token,
		                planningAll: localStoragePwa.planningAll,
		                planningTodayAndTomorrow: true,
		                fromLogin: localStoragePwa.fromLogin,
		                synchronization: this.$moment(new Date()).format('DD/MM/YYYY - HH:mm')
	                }))

	                // Check application version
	                await this.checkApplicationVersion(response.headers['x-application-version'])
	                // Store files in cache
	                await this.storeInCache()

	                await this.$store.dispatch('alerts/success', 'Mise à jour 2J effectuée')
	                await this.$router.push({ name: 'Pwa Planning' })
                })
            } catch (e) {
                localStorage.setItem('pwa', JSON.stringify({
                    isAuthenticated: localStoragePwa.isAuthenticated,
                    access_token: localStoragePwa.access_token,
	                planningAll: localStoragePwa.planningAll,
	                planningTodayAndTomorrow: false,
	                fromLogin: localStoragePwa.fromLogin,
	                synchronization: localStoragePwa.synchronization
                }))
            }
	        this.updateOfflineDataIndications()
            this.loadingDay = false
        },
        async getPlanning() {
            this.loading = true
            const localStoragePwa = JSON.parse(localStorage.getItem('pwa'))
            try {
                // Check if last update is older than today
                if (this.$moment(localStoragePwa.synchronization, 'DD/MM/YYYY - HH:mm').isBefore(this.$moment().startOf('day'))) {
                    // If so then clear everything in data table
                    await this.clearIndexedDBTable('data')
                }
                await this.$store.dispatch('pwaPlanning/getPlanning', {
                    access_token: localStoragePwa.access_token,
                    technician_id: this.technician.id,
	                period: 6
                }).then(async (response) => {
	                localStorage.setItem('pwa', JSON.stringify({
		                isAuthenticated: localStoragePwa.isAuthenticated,
		                access_token: localStoragePwa.access_token,
		                planningAll: true,
		                planningTodayAndTomorrow: true,
		                fromLogin: localStoragePwa.fromLogin,
		                synchronization: this.$moment(new Date()).format('DD/MM/YYYY - HH:mm')
	                }))

	                // Check application version
	                await this.checkApplicationVersion(response.headers['x-application-version'])
	                // Store files in cache
	                await this.storeInCache()

	                await this.$store.dispatch('alerts/success', 'Mise à jour 6J effectuée')
	                await this.$router.push({ name: 'Pwa Planning' })
                })
            } catch (e) {}
	        this.updateOfflineDataIndications()
	        this.loading = false
        },
	    updateOfflineDataIndications() {
		    const localStoragePwa = JSON.parse(localStorage.getItem('pwa'))
		    this.planning.all = localStoragePwa.planningAll
		    this.planning.todayAndTomorrow = localStoragePwa.planningTodayAndTomorrow
	    }
    },
    async mounted() {
	    const localStoragePwa = JSON.parse(localStorage.getItem('pwa'))

	    if(JSON.parse(localStorage.getItem('pwa')).fromLogin) {
		    await this.clearIndexedDBTable('data')

		    localStorage.setItem('pwa', JSON.stringify({
			    isAuthenticated: localStoragePwa.isAuthenticated,
			    access_token: localStoragePwa.access_token,
			    planningAll: localStoragePwa.planningAll,
			    planningTodayAndTomorrow: localStoragePwa.planningTodayAndTomorrow,
			    synchronization: localStoragePwa.synchronization,
			    fromLogin: false
		    }))
	    }

	    this.getCacheVersion().then(cacheVersion => {
		    this.cacheVersion = cacheVersion
	    })

        if(!localStoragePwa.planningTodayAndTomorrow) {
            await this.getTodayAndTomorrow()
        } else {
			this.updateOfflineDataIndications()
        }

		this.$emit('countPatchRequests')
    },
	watch: {
		isLoading(newVal) {
			this.loading = newVal
		}
	}
}
</script>

<style scoped lang="scss">

</style>
